import React from "react";
import { ArrowRight, Check } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import {
  Em,
  Eyebrow,
  GhostButton,
  GradientButton,
  Headline,
  Panel,
  Reveal,
  Section,
  SERIF,
  Subhead,
} from "../components/marketing/primitives";
import {
  CONTROL_COUNT,
  COVERAGE_LABEL,
  CTA_LEVELS,
  Coverage,
  DOMAINS,
  EVIDENCE_TIERS,
  MATURITY,
  STANDARDS,
} from "../components/marketing/content";
import { usePageMeta } from "../components/marketing/usePageMeta";

/**
 * /code-trust: the Nucleus Systems Code Trust Assurance Framework (NS-CTAF
 * v1.0) and where Paxley supplies its evidence.
 *
 * The coverage marks are deliberate and conservative: "strong" only where
 * the platform produces the evidence today, "partial" where it covers some
 * controls, "assessed" where Nucleus Systems reviews the domain in an
 * assessment. Never upgrade a mark without the feature shipping.
 *
 * Eyebrows: two (framework, levels).
 */
export default function CodeTrust() {
  usePageMeta(
    "Code Trust Assurance (NS-CTAF) | Paxley",
    `Six domains, ${CONTROL_COUNT} controls and a 0-100 Trust Score for software supply chain security, with Paxley as the evidence layer. Aligned with SLSA, NIST SSDF, OWASP SAMM, EU CRA and EO 14028.`
  );
  return (
    <MarketingLayout>
      <HeroSection />
      <ModelSection />
      <DomainsSection />
      <LevelsSection />
      <FinalCTA />
    </MarketingLayout>
  );
}

const ASSESS_LINK = "/inquiry?interest=code-trust";

function HeroSection() {
  return (
    <Section padding="hero" container="narrow">
      <div className="text-center">
        <Headline size="hero" className="mx-auto">
          Code trust you can <Em>measure and prove.</Em>
        </Headline>
        <Subhead size="large" className="mx-auto mt-6 max-w-xl">
          A structured, independently assessable model for software supply chain security, with
          Paxley producing the evidence.
        </Subhead>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GradientButton to={ASSESS_LINK} size="lg">
            Book an assessment
          </GradientButton>
          <GhostButton to="/onboarding" size="lg">
            Start free scan
          </GhostButton>
        </div>
      </div>

      <Reveal className="mx-auto mt-14 grid max-w-2xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#E4DFD3] bg-[#E4DFD3] sm:grid-cols-4">
        {[
          { k: String(CONTROL_COUNT), v: "controls" },
          { k: "6", v: "domains" },
          { k: "5", v: "maturity levels" },
          { k: "0-100", v: "Trust Score" },
        ].map((s) => (
          <div key={s.v} className="bg-white px-4 py-5 text-center">
            <p className="text-3xl" style={SERIF}>
              {s.k}
            </p>
            <p className="mt-1 text-xs opacity-55">{s.v}</p>
          </div>
        ))}
      </Reveal>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// How the model scores: green panel, four parts on hairlines
// ──────────────────────────────────────────────────────────────────

function ModelSection() {
  return (
    <Section container="none" padding="tight">
      <Panel tone="green">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <Reveal className="max-w-2xl">
            <Eyebrow className="!opacity-70">The framework</Eyebrow>
            <Headline size="section" className="mt-4">
              From scanner output <Em>to a trust score.</Em>
            </Headline>
            <Subhead className="mt-4 !opacity-80">
              NS-CTAF covers contributor identity, build integrity, secure development, dependency
              management, runtime behaviour and governance, and maps to {STANDARDS.join(", ")}.
            </Subhead>
          </Reveal>

          <div className="mt-12 grid gap-x-12 md:grid-cols-2">
            <Reveal>
              <div className="border-t border-white/15 py-6">
                <h3 className="text-base font-semibold">Maturity, L1 to L5</h3>
                <ol className="mt-4 grid grid-cols-5 gap-2 text-center">
                  {MATURITY.map((m) => (
                    <li key={m.level} className="rounded-xl bg-white/10 px-1 py-3">
                      <p className="text-xl" style={SERIF}>
                        {m.level}
                      </p>
                      <p className="mt-1 text-[10px] leading-tight opacity-70 sm:text-[11px]">{m.name}</p>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 text-sm leading-relaxed opacity-70">
                  Every control is scored on its own against each level. Domain maturity is the
                  weighted average of its controls.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="border-t border-white/15 py-6">
                <h3 className="text-base font-semibold">Trust Score, 0 to 100</h3>
                <div className="mt-4 flex h-3 overflow-hidden rounded-full" aria-hidden="true">
                  {DOMAINS.map((d, i) => (
                    <span
                      key={d.id}
                      className="border-r-2 border-[#1E4D38] last:border-r-0"
                      style={{ width: `${d.weight}%`, backgroundColor: `rgba(245,241,230,${0.85 - i * 0.12})` }}
                    />
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed opacity-70">
                  Each domain score is multiplied by its weight:{" "}
                  {DOMAINS.map((d) => `${d.id} ${d.weight}%`).join(", ")}. Seven hard gates must
                  pass before a level is awarded, whatever the overall score.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border-t border-white/15 py-6">
                <h3 className="text-base font-semibold">Five scoring axes per control</h3>
                <p className="mt-3 text-sm leading-relaxed opacity-70">
                  Coverage, automation, integration, verification and continuous assurance. A
                  control that exists on paper but is not automated or verified scores lower.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="border-t border-white/15 py-6">
                <h3 className="text-base font-semibold">Evidence tiers, T1 to T4</h3>
                <ul className="mt-3 space-y-1.5 text-sm opacity-80">
                  {EVIDENCE_TIERS.map((t) => (
                    <li key={t.tier}>
                      <span className="mr-2 font-mono text-xs opacity-60">{t.tier}</span>
                      {t.name}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm leading-relaxed opacity-70">
                  L4 and above need T3 or T4 evidence. Paxley scans produce T3: automated,
                  repeatable pipeline output, every finding fingerprinted and every report hashed.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </Panel>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// The six domains: every control listed, with what Paxley contributes
// ──────────────────────────────────────────────────────────────────

const MARK: Record<Coverage, string> = {
  strong: "border-[#1E4D38] bg-[#1E4D38] text-[#F5F1E6]",
  partial: "border-[#1E4D38]/40 bg-[#E4EFE4] text-[#1E4D38]",
  assessed: "border-[#17150F]/20 bg-white text-[#17150F]/70",
};

function DomainsSection() {
  return (
    <Section>
      <Reveal className="max-w-2xl">
        <Headline size="section">
          Six domains. <Em>{CONTROL_COUNT} controls.</Em>
        </Headline>
        <Subhead className="mt-4">
          What each domain covers, and where Paxley supplies the evidence today. The rest is
          reviewed by Nucleus Systems in a Code Trust assessment.
        </Subhead>
        <div className="mt-6 flex flex-wrap gap-2">
          {(Object.keys(COVERAGE_LABEL) as Coverage[]).map((c) => (
            <span key={c} className={`rounded-full border px-3 py-1 text-xs font-medium ${MARK[c]}`}>
              {COVERAGE_LABEL[c]}
            </span>
          ))}
        </div>
      </Reveal>

      <div className="mt-12 space-y-4">
        {DOMAINS.map((d, i) => (
          <Reveal key={d.id} delay={0.03 * i}>
            <details className="group rounded-2xl border border-[#E4DFD3] bg-white open:shadow-[0_14px_36px_-24px_rgba(30,77,56,0.35)]">
              <summary className="grid cursor-pointer list-none grid-cols-[3rem_1fr_auto] items-center gap-4 px-5 py-5 sm:px-7 [&::-webkit-details-marker]:hidden">
                <span className="text-2xl opacity-40" style={SERIF}>
                  {d.id}
                </span>
                <span>
                  <span className="block text-lg font-semibold">{d.name}</span>
                  <span className="mt-0.5 block text-xs opacity-55">
                    {d.controls.length} controls, {d.weight}% of the Trust Score
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <span className={`hidden rounded-full border px-3 py-1 text-xs font-medium md:inline ${MARK[d.coverage]}`}>
                    {COVERAGE_LABEL[d.coverage]}
                  </span>
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#E4DFD3] text-sm leading-none opacity-60 transition-transform duration-300 group-open:rotate-45"
                    aria-hidden="true"
                  >
                    +
                  </span>
                </span>
              </summary>
              <div className="grid gap-8 border-t border-[#E4DFD3] px-5 py-6 sm:px-7 lg:grid-cols-[1fr_1.4fr]">
                <div>
                  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium md:hidden ${MARK[d.coverage]}`}>
                    {COVERAGE_LABEL[d.coverage]}
                  </span>
                  <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] opacity-50 md:mt-0">
                    What Paxley contributes
                  </p>
                  <p className="mt-2 text-sm leading-relaxed opacity-80">{d.paxley}</p>
                </div>
                <ol className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                  {d.controls.map((c, n) => (
                    <li key={c} className="flex gap-2">
                      <span className="w-5 shrink-0 text-right font-mono text-xs leading-5 opacity-40">{n + 1}</span>
                      <span className="opacity-85">{c}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </details>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// CTA levels: coal panel, four steps as a numbered ladder
// ──────────────────────────────────────────────────────────────────

function LevelsSection() {
  return (
    <Section container="none" padding="tight">
      <Panel tone="coal">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Eyebrow className="!opacity-60">Trust levels</Eyebrow>
              <Headline size="section" className="mt-4">
                Four levels. <Em>One direction.</Em>
              </Headline>
              <Subhead className="mt-4 !opacity-70">
                A Code Trust level is awarded on thresholds and hard gates, so customers,
                regulators and acquirers can read it the same way.
              </Subhead>
            </Reveal>
            <ol className="lg:col-span-7">
              {CTA_LEVELS.map((l, i) => (
                <Reveal key={l.id} delay={0.06 * i}>
                  <li className="grid grid-cols-[5.5rem_1fr] gap-4 border-t border-white/15 py-6 first:border-t-0 first:pt-0">
                    <span className="font-mono text-sm opacity-60">{l.id}</span>
                    <div>
                      <h3 className="text-xl" style={SERIF}>
                        {l.name}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed opacity-70">{l.body}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </Panel>
    </Section>
  );
}

function FinalCTA() {
  return (
    <Section padding="default" container="narrow">
      <Reveal className="text-center">
        <Headline size="section">
          Where does your code <Em>stand today?</Em>
        </Headline>
        <Subhead className="mx-auto mt-4 max-w-xl">
          A Code Trust assessment scores your estate against NS-CTAF, with Paxley scans as the
          evidence and a roadmap to the next level.
        </Subhead>
        <ul className="mx-auto mt-8 flex max-w-xl flex-col gap-2 text-left text-sm sm:items-center">
          {[
            "Trust Score and level per application",
            "Evidence pack mapped to each control",
            "Prioritised roadmap to the next level",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2.5">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
              <span className="opacity-85">{f}</span>
            </li>
          ))}
        </ul>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GradientButton to={ASSESS_LINK} size="lg" icon={<ArrowRight className="h-4 w-4" />}>
            Book an assessment
          </GradientButton>
          <GhostButton to="/why-paxley" size="lg">
            Why Paxley
          </GhostButton>
        </div>
        <p className="mx-auto mt-8 max-w-xl text-xs leading-relaxed opacity-50">
          Evidence mapping supports control review. It is not certification, an audit opinion or
          legal advice.
        </p>
      </Reveal>
    </Section>
  );
}
